import axios from 'axios';
import { getStoredRef } from '../utils/referralRef';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const ref = getStoredRef();
    if (ref) {
      config.headers['X-Referral-Ref'] = ref;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// One refresh at a time: requests that fail together wait for the same new tokens. The server
// issues a new refresh token on every refresh; saving it keeps an active user signed in instead
// of logging them out when the first refresh token expires (7 days).
let refreshing = null;
const refreshSession = () => {
  if (!refreshing) {
    refreshing = (async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const { data } = await axios.post(`${process.env.REACT_APP_API_URL || '/api'}/auth/refresh`, { refresh_token: refreshToken });
      localStorage.setItem('accessToken', data.access_token);
      if (data.refresh_token) localStorage.setItem('refreshToken', data.refresh_token);
      if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
      return data.access_token;
    })().finally(() => { refreshing = null; });
  }
  return refreshing;
};

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Auth endpoints answer 401 to mean "these credentials are refused", not
    // "your session expired" — refreshing and reloading would swallow the
    // reason the login was denied.
    // (the confirmation code is typed by a signed-in person, so its 401 is an expired session)
    const isAuthRequest = originalRequest?.url?.startsWith('/auth/') && !originalRequest.url.startsWith('/auth/email-code/');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshSession();
        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const registration = {
  register: (email, password, { acceptTerms, pdConsent, legalVersion } = {}) =>
    api.post('/auth/register', { email, password, accept_terms: !!acceptTerms, pd_consent: !!pdConsent, legal_version: legalVersion }),
  verifyEmail: (token) => api.get('/auth/verify-email', { params: { token } }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  // the signed-in person types the code from the letter / asks for a new one
  verifyCode: (code) => api.post('/auth/email-code/verify', { code }),
  resendCode: () => api.post('/auth/email-code/resend'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

export const dashboardApi = {
  get: (params) => api.get('/dashboard', { params }),
};

export const analyticsApi = {
  master: (params) => api.get('/analytics/wb/master', { params }),
  operations: (params) => api.get('/analytics/wb/operations', { params }),
  reconciliation: (params) => api.get('/analytics/wb/reconciliation', { params }),
  stocks: (params) => api.get('/analytics/wb/stocks', { params }),
  productCharges: (params) => api.get('/analytics/wb/product-charges', { params }),
  promotions: (params) => api.get('/analytics/wb/promotions', { params }),
  promotionCabinetItems: (data) => api.post('/analytics/wb/promotions/cabinet-items', data),
  ozonReports: (params) => api.get('/analytics/ozon/reports', { params }),
  ozonRealizationRows: (params) => api.get('/analytics/ozon/realization-rows', { params }),
};

export const exportsApi = {
  types: () => api.get('/exports/wb/types'),
  build: (params) => api.get('/exports/wb', { params }),
};

export const syncApi = {
  status: (accountId) => api.get('/sync/status', { params: accountId ? { account_id: accountId } : {} }),
  retry: (accountId, key) => api.post(`/sync/accounts/${accountId}/sources/${key}/retry`),
  requestHistory: (accountId, from) => api.post(`/sync/accounts/${accountId}/history`, { from }),
};

export const notificationsApi = {
  list: () => api.get('/notifications'),
  criticalNews: () => api.get('/news/critical'),
};

export const costsApi = {
  list: (accountId) => api.get('/costs', { params: { account_id: accountId } }),
  set: (accountId, validFrom, items) => api.post('/costs', { account_id: accountId, valid_from: validFrom, items }),
  history: (accountId, nmId) => api.get('/costs/history', { params: { account_id: accountId, nm_id: nmId } }),
  deletePeriod: (id) => api.delete(`/costs/periods/${id}`),
};

export const teamApi = {
  list: () => api.get('/team'),
  create: (data) => api.post('/team', data),
  update: (id, data) => api.put(`/team/${id}`, data),
  remove: (id) => api.delete(`/team/${id}`),
};

export const settingsApi = {
  me: () => api.get('/me'),
  updateMe: (fullName) => api.put('/me', { full_name: fullName }),
  changePassword: (current, next) => api.post('/me/password', { current_password: current, new_password: next }),
  taxes: (marketplace) => api.get('/settings/taxes', { params: { marketplace } }),
  saveTax: (data) => api.post('/settings/taxes', data),
  deleteTax: (id) => api.delete(`/settings/taxes/${id}`),
};

// Prices come only from the server: the page sends the selection, never amounts.
export const tariffsApi = {
  catalog: () => api.get('/tariffs'),
  quote: (selection) => api.post('/tariffs/quote', selection),
  createRequest: (selection) => api.post('/tariffs/requests', selection),
  myRequests: () => api.get('/tariffs/requests'),
  pay: (requestId) => api.post(`/tariffs/requests/${requestId}/pay`),
  payment: (id) => api.get(`/tariffs/payments/${id}`),
  limits: () => api.get('/tariffs/limits'),
  // the free trial: may this account take it, and take it
  trial: () => api.get('/tariffs/trial'),
  claimTrial: () => api.post('/tariffs/trial'),
};

export const billingAdminApi = {
  requests: (status) => api.get('/admin/tariff-requests', { params: status ? { status } : {} }),
  setRequestStatus: (id, status, adminNote) => api.patch(`/admin/tariff-requests/${id}`, { status, admin_note: adminNote }),
  promos: () => api.get('/admin/promo-codes'),
  createPromo: (data) => api.post('/admin/promo-codes', data),
  updatePromo: (id, data) => api.put(`/admin/promo-codes/${id}`, data),
  deletePromo: (id) => api.delete(`/admin/promo-codes/${id}`),
};

// Partner (referral) program.
export const referralApi = {
  click: (code) => api.post('/referral/click', { code }),
  program: () => api.get('/referral/program'),
  status: () => api.get('/referral/status'),
  me: () => api.get('/referral/me'),
  join: (data) => api.post('/referral/me', data),
  update: (data) => api.put('/referral/me', data),
  setPayout: (data) => api.put('/referral/me/payout', data),
};

export const referralAdminApi = {
  program: () => api.get('/admin/referral/program'),
  setProgram: (active) => api.put('/admin/referral/program', { active }),
  agents: (deleted = false) => api.get('/admin/referral/agents', { params: deleted ? { deleted: 1 } : {} }),
  agent: (id) => api.get(`/admin/referral/agents/${id}`),
  setAgentStatus: (id, status, reason) => api.patch(`/admin/referral/agents/${id}`, { status, reason }),
  pay: (id, note) => api.post(`/admin/referral/agents/${id}/payouts`, { note }),
  // deleting an agent is an archive: a reason is required; restoring brings the agent back
  deleteAgent: (id, reasonCode, comment) => api.post(`/admin/referral/agents/${id}/delete`, { reason_code: reasonCode, comment }),
  restoreAgent: (id) => api.post(`/admin/referral/agents/${id}/restore`),
  alerts: () => api.get('/admin/referral/alerts'),
  dismissAlert: (id) => api.post(`/admin/referral/alerts/${id}/dismiss`),
  setClientStatus: (id, status, reason) => api.patch(`/admin/referral/clients/${id}`, { status, reason }),
  cancelCommission: (id, reason) => api.post(`/admin/referral/commissions/${id}/cancel`, { reason }),
  sync: () => api.post('/admin/referral/sync'),
};

export const appApi = {
  config: () => api.get('/app/config'),
};

export default api;


